/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The lifecycle state of the key. pending: published for verifier cache warm-up but not signing yet. active: the key new signatures use; at most one per set. retired: no longer signing, still published so outstanding tokens keep verifying. revoked: withdrawn entirely; tokens signed with it stop verifying.
 */
export const KeyState = {
  Pending: "pending",
  Active: "active",
  Retired: "retired",
  Revoked: "revoked",
} as const;
/**
 * The lifecycle state of the key. pending: published for verifier cache warm-up but not signing yet. active: the key new signatures use; at most one per set. retired: no longer signing, still published so outstanding tokens keep verifying. revoked: withdrawn entirely; tokens signed with it stop verifying.
 */
export type KeyState = OpenEnum<typeof KeyState>;

/**
 * A published JSON Web Key inside a key set.
 */
export type JSONWebKey = {
  /**
   * When the key last became the set's active signing key.
   */
  activatedAt?: Date | undefined;
  /**
   * When the key was published.
   */
  createdAt: Date;
  /**
   * The external key the key was minted from. Signing for this kid always uses exactly this key, even after the set is re-pointed at another external key.
   */
  externalKeyId: string;
  /**
   * The ID of the published key.
   */
  id: string;
  /**
   * The key set the key is published in.
   */
  jsonWebKeySetId: string;
  /**
   * The lifecycle state of the key. pending: published for verifier cache warm-up but not signing yet. active: the key new signatures use; at most one per set. retired: no longer signing, still published so outstanding tokens keep verifying. revoked: withdrawn entirely; tokens signed with it stop verifying.
   */
  keyState: KeyState;
  /**
   * The key ID: the RFC 7638 SHA-256 thumbprint of the public key, base64url-encoded. Carried in the JWK document and in the header of every token signed with the key.
   */
  kid: string;
  /**
   * The organization that owns the key.
   */
  organizationId: string;
  /**
   * The public JWK document (RFC 7517) as published, including kid, alg and use.
   */
  publicJwk: any;
  /**
   * When the key last left active signing use.
   */
  retiredAt?: Date | undefined;
  /**
   * When the key was revoked.
   */
  revokedAt?: Date | undefined;
  /**
   * When the key was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const KeyState$inboundSchema: z.ZodMiniType<KeyState, unknown> =
  openEnums.inboundSchema(KeyState);

/** @internal */
export const JSONWebKey$inboundSchema: z.ZodMiniType<JSONWebKey, unknown> = z
  .pipe(
    z.object({
      activated_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      external_key_id: z.string(),
      id: z.string(),
      json_web_key_set_id: z.string(),
      key_state: KeyState$inboundSchema,
      kid: z.string(),
      organization_id: z.string(),
      public_jwk: z.any(),
      retired_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      revoked_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "activated_at": "activatedAt",
        "created_at": "createdAt",
        "external_key_id": "externalKeyId",
        "json_web_key_set_id": "jsonWebKeySetId",
        "key_state": "keyState",
        "organization_id": "organizationId",
        "public_jwk": "publicJwk",
        "retired_at": "retiredAt",
        "revoked_at": "revokedAt",
        "updated_at": "updatedAt",
      });
    }),
  );

export function jsonWebKeyFromJSON(
  jsonString: string,
): SafeParseResult<JSONWebKey, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => JSONWebKey$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'JSONWebKey' from JSON`,
  );
}
